import React, { useState, useEffect } from "react";
import { cajaBancosAPI, type Banco } from "@/lib/api";
import { toast } from "sonner";
import { ListNewLoading, ListToolbar } from "@/components/ListNewButton";

interface BancosListadoProps {
  onEdit?: (banco: Banco) => void;
  onNew?: () => void;
}

const tipoCuentaLabels: Record<string, string> = {
  CC_Pesos: "Cuenta Corriente Pesos",
  CA_Pesos: "Caja de Ahorro Pesos",
  CC_Dolares: "Cuenta Corriente Dólares",
  CA_Dolares: "Caja de Ahorro Dólares",
};

export function BancosListado({ onEdit, onNew }: BancosListadoProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await cajaBancosAPI.listBancos();
        setBancos(data);
      } catch (error) {
        console.error("Error loading bancos:", error);
        toast.error("Error al cargar bancos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredBancos = bancos.filter(b =>
    !searchQuery ||
    b.codigo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.nombreBanco?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCurrency = (amount: number, tipoCuenta?: string) => {
    const isDollar = tipoCuenta?.includes('Dolar');
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: isDollar ? 'USD' : 'ARS',
    }).format(amount);
  };

  if (loading) {
    return <ListNewLoading onNew={onNew} message="Cargando bancos..." />;
  }

  return (
    <div className="space-y-4 mt-4">
      <ListToolbar onNew={onNew}>
        <label className="block text-sm font-medium mb-1">Buscar</label>
        <input
          type="text"
          placeholder="Buscar por código o nombre..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-4 py-2 border border-input bg-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
        />
      </ListToolbar>

      {filteredBancos.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          No se encontraron bancos
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2 text-left">Código</th>
                <th className="px-4 py-2 text-left">Banco</th>
                <th className="px-4 py-2 text-left">Tipo Cuenta</th>
                <th className="px-4 py-2 text-right">Saldo Inicial</th>
                <th className="px-4 py-2 text-right">Saldo Actual</th>
              </tr>
            </thead>
            <tbody>
              {filteredBancos.map((banco) => (
                <tr
                  key={banco.codigo}
                  className="border-b hover:bg-muted/50 cursor-pointer"
                  onClick={() => onEdit?.(banco)}
                >
                  <td className="px-4 py-2">{banco.codigo}</td>
                  <td className="px-4 py-2">{banco.nombreBanco}</td>
                  <td className="px-4 py-2">{tipoCuentaLabels[banco.tipoCuenta] || banco.tipoCuenta}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(banco.saldoInicial, banco.tipoCuenta)}</td>
                  <td className="px-4 py-2 text-right">{formatCurrency(banco.saldoActual, banco.tipoCuenta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}